<div class="bs-page">
  <!-- ── Loading ─────────────────────────────────────────────────────────── -->
  @if (phase === 'loading') {
  <div class="bs-splash">
    <div class="bs-spinner"></div>
    <p>{{ "BOOK_APPOINTMENT.LOADING" | translate }}</p>
  </div>
  }

  <!-- ── Error ───────────────────────────────────────────────────────────── -->
  @if (phase === 'error') {
  <div class="bs-splash bs-splash--error">
    <div class="bs-splash__icon">&#9888;</div>
    <p>{{ errorText }}</p>
  </div>
  }

  <!-- ── Main shell ──────────────────────────────────────────────────────── -->
  @if (phase !== 'loading' && phase !== 'error') {
  <div class="bs-shell">
    <!-- Left sidebar -->
    <aside class="bs-sidebar">
      <div class="bs-sidebar__avatar">{{ companyInitial }}</div>
      <h1 class="bs-sidebar__company">{{ companyName }}</h1>
      <div class="bs-sidebar__meta">
        <div class="bs-meta-item">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          60 min
        </div>
        <div class="bs-meta-item">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          Sagel Meet
        </div>
      </div>
      <!-- Selected date/time pill (shown in details phase) -->
      @if (phase === 'details' && selectedDate && selectedSlot) {
      <div class="bs-sidebar__selection">
        <div class="bs-sidebar__sel-date">{{ formattedSelectedDate }}</div>
        <div class="bs-sidebar__sel-time">{{ selectedSlot }}</div>
        <div class="bs-sidebar__sel-tz">{{ timezone }}</div>
      </div>
      }
      <div class="bs-powered-by">
        <span class="bs-powered-by__label">{{
          "BOOK_APPOINTMENT.POWERED_BY" | translate
        }}</span>
        <img
          class="bs-powered-by__logo"
          src="assets/img/brand/SAGEL_LOGO.svg"
          alt="Sagel"
        />
      </div>
    </aside>
    <!-- Right content panel -->
    <div class="bs-content">
      <!-- ── Phase: pick ───────────────────────────────────────────────── -->
      @if (phase === 'pick') {
      <div class="bs-pick">
        <h2 class="bs-pick__title">
          {{ "BOOK_APPOINTMENT.SELECT_DATETIME" | translate }}
        </h2>
        <div class="bs-pick__body">
          <!-- Inline calendar -->
          <div class="bs-cal">
            <div class="bs-cal__nav">
              <button
                class="bs-cal__nav-btn"
                type="button"
                (click)="prevMonth()"
                aria-label="Previous month"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <span class="bs-cal__month-label">{{ monthTitle }}</span>
              <button
                class="bs-cal__nav-btn"
                type="button"
                (click)="nextMonth()"
                aria-label="Next month"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
            <div class="bs-cal__weekdays">
              @for (d of weekDayLabels; track d) {
              <span>{{ d }}</span>
              }
            </div>
            <div class="bs-cal__grid">
              @for (week of calendarWeeks; track $index) { @for (day of week;
              track $index) {
              <button
                type="button"
                class="bs-cal__day"
                [class.bs-cal__day--other]="!day.isCurrentMonth"
                [class.bs-cal__day--today]="day.isToday"
                [class.bs-cal__day--selected]="isSelectedDate(day.date)"
                [disabled]="day.isPast || !day.isCurrentMonth"
                (click)="selectDate(day.date)"
              >
                {{ day.date.getDate() }}
              </button>
              } }
            </div>
          </div>
          <!-- Time slot list (appears when a date is selected) -->
          @if (selectedDate) {
          <div class="bs-slots">
            <div class="bs-slots__date">{{ formattedSelectedDate }}</div>
            <div class="bs-slots__scroll">
              @for (slot of daySlots; track slot) {
              <button
                type="button"
                class="bs-slot-btn"
                [class.bs-slot-btn--occupied]="isSlotOccupied(slot)"
                [disabled]="isSlotOccupied(slot)"
                (click)="selectSlot(slot)"
              >
                {{ slot }}
              </button>
              } @if (availableSlots.length === 0) {
              <p class="bs-slots__empty">
                {{ "BOOK_APPOINTMENT.NO_TIMES" | translate }}
              </p>
              }
            </div>
          </div>
          }
          <!-- Placeholder when no date selected -->
          @if (!selectedDate) {
          <div class="bs-slots bs-slots--hint">
            <p class="bs-slots__hint">
              {{ "BOOK_APPOINTMENT.PICK_DATE_HINT" | translate }}
            </p>
          </div>
          }
        </div>
        <!-- Timezone selector -->
        <div class="bs-tz">
          <button
            type="button"
            class="bs-tz__pill"
            (click)="showTzPicker = !showTzPicker"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path
                d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
              />
            </svg>
            {{ timezone }}
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          @if (showTzPicker) {
          <div class="bs-tz__dropdown">
            <input
              class="bs-tz__search"
              type="text"
              [(ngModel)]="tzSearch"
              name="tzSearch"
              [placeholder]="'BOOK_APPOINTMENT.SEARCH_TZ' | translate"
              autofocus
            />
            <div class="bs-tz__list">
              @for (tz of filteredTimezones; track tz) {
              <button
                type="button"
                class="bs-tz__option"
                [class.bs-tz__option--active]="tz === timezone"
                (click)="selectTimezone(tz)"
              >
                {{ tz }}
              </button>
              } @if (filteredTimezones.length === 0) {
              <p class="bs-tz__empty">
                {{ "BOOK_APPOINTMENT.NO_TZ_RESULTS" | translate }}
              </p>
              }
            </div>
          </div>
          }
        </div>
      </div>
      }
      <!-- ── Phase: details ────────────────────────────────────────────── -->
      @if (phase === 'details') {
      <div class="bs-details">
        <button class="bs-back" type="button" (click)="backToCalendar()">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          {{ "BOOK_APPOINTMENT.BACK" | translate }}
        </button>
        <h2 class="bs-details__title">
          {{ "BOOK_APPOINTMENT.ENTER_DETAILS" | translate }}
        </h2>
        @if (formError) {
        <div class="bs-form-error">{{ formError }}</div>
        }
        <form (ngSubmit)="onSubmit()" class="bs-form">
          <div class="bs-field">
            <label class="bs-label"
              >{{ "BOOK_APPOINTMENT.FULL_NAME" | translate }}
              <span class="bs-req">*</span></label
            >
            <input
              class="bs-input"
              type="text"
              [(ngModel)]="name"
              name="name"
              [placeholder]="'BOOK_APPOINTMENT.NAME_PLACEHOLDER' | translate"
              required
            />
          </div>
          <div class="bs-field">
            <label class="bs-label"
              >{{ "BOOK_APPOINTMENT.EMAIL_ADDRESS" | translate }}
              <span class="bs-req">*</span></label
            >
            <input
              class="bs-input"
              type="email"
              [(ngModel)]="email"
              name="email"
              [placeholder]="'BOOK_APPOINTMENT.EMAIL_PLACEHOLDER' | translate"
              required
            />
          </div>
          <div class="bs-field">
            <label class="bs-label"
              >{{ "BOOK_APPOINTMENT.PHONE" | translate }}
              <span class="bs-opt">{{
                "BOOK_APPOINTMENT.OPTIONAL" | translate
              }}</span></label
            >
            <input
              class="bs-input"
              type="tel"
              [(ngModel)]="phone"
              name="phone"
              [placeholder]="'BOOK_APPOINTMENT.PHONE_PLACEHOLDER' | translate"
            />
          </div>
          <div class="bs-field">
            <label class="bs-label"
              >{{ "BOOK_APPOINTMENT.NOTES" | translate }}
              <span class="bs-opt">{{
                "BOOK_APPOINTMENT.OPTIONAL" | translate
              }}</span></label
            >
            <textarea
              class="bs-textarea"
              [(ngModel)]="notes"
              name="notes"
              rows="3"
              [placeholder]="'BOOK_APPOINTMENT.NOTES_PLACEHOLDER' | translate"
            ></textarea>
          </div>
          <button
            type="submit"
            class="bs-confirm-btn"
            [disabled]="isSubmitting"
          >
            @if (isSubmitting) {
            <span class="bs-spinner bs-spinner--sm"></span>
            }
            {{
              (isSubmitting
                ? "BOOK_APPOINTMENT.SUBMITTING"
                : "BOOK_APPOINTMENT.CONFIRM_BOOKING"
              ) | translate
            }}
          </button>
        </form>
      </div>
      }
      <!-- ── Phase: success ────────────────────────────────────────────── -->
      @if (phase === 'success') {
      <div class="bs-success">
        <div class="bs-success__check">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 class="bs-success__title">
          {{ "BOOK_APPOINTMENT.SUCCESS_TITLE" | translate }}
        </h2>
        <p class="bs-success__sub">
          {{ "BOOK_APPOINTMENT.SUCCESS_SUB" | translate }}
          <strong>{{ email }}</strong
          >.
        </p>
        <div class="bs-success__card">
          <div class="bs-success__row">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {{ formattedSelectedDate }}
          </div>
          <div class="bs-success__row">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {{ selectedSlot }} · 60 min
          </div>
          <div class="bs-success__row">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {{ companyName }}
          </div>
        </div>
        <p class="bs-success__hint">
          {{ "BOOK_APPOINTMENT.SUCCESS_HINT" | translate }}
        </p>
      </div>
      }
    </div>
    <!-- /bs-content -->
  </div>
  }<!-- /bs-shell -->
</div>
<!-- /bs-page -->
